import { Link } from "react-router-dom";
import { AlertTriangle, Clock, CalendarCheck, PhoneCall, ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBand from "@/components/CtaBand";
import { Reveal, Eyebrow } from "@/components/Reveal";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { CLINIC, SITE_URL } from "@/data/content";

const SECTIONS = [
  {
    h: "What is HIV PEP?",
    b: "HIV PEP (post-exposure prophylaxis) is a course of HIV medicines taken after a potential exposure to HIV, to reduce the chance of infection. It is an emergency preventive treatment — not a routine one — and it must be started quickly.",
  },
  {
    h: "Who may need PEP?",
    b: "PEP may be considered after a recent potential HIV exposure — for example, condomless sex with a partner whose HIV status is positive or unknown, condom breakage, or certain other exposures. Whether PEP is appropriate for you depends on the type of exposure, its timing and clinical assessment. A consultation helps you get a clear answer quickly.",
  },
  {
    h: "Why timing matters",
    b: "PEP should be started as soon as possible after a potential HIV exposure and is generally not recommended after 72 hours. Every hour matters — if you think you may need PEP, seek medical advice now rather than waiting to see.",
    urgent: true,
  },
  {
    h: "What happens during a PEP consultation?",
    b: "Dr. Ankur Lal will assess the exposure privately and without judgment, determine whether PEP is clinically indicated, explain how to take it correctly, and discuss possible side effects. When PEP is prescribed, the standard course is 28 days. Baseline and follow-up testing are planned at the same time.",
  },
  {
    h: "HIV testing and follow-up",
    b: "HIV testing is recommended at baseline and at intervals after completing PEP to confirm your status. Follow-up can usually be done online. If ongoing risk exists, HIV PrEP may be discussed as a longer-term prevention option.",
  },
  {
    h: "Confidential PEP consultation with Dr. Ankur Lal",
    b: "PEP consultations — online or in clinic — are completely private and judgment-free. Dr. Ankur Lal is an STD & Sexual Health Specialist and Ex-STD Specialist & Former Senior Resident, AIIMS New Delhi. You will be guided by a specialist, quickly and discreetly.",
  },
];

const PEP_FAQS = [
  {
    q: "How soon should I start HIV PEP?",
    a: "As soon as possible after a potential exposure — ideally within hours. PEP is generally not recommended after 72 hours. Do not wait for symptoms; seek medical advice immediately.",
  },
  {
    q: "Does PEP guarantee I won't get HIV?",
    a: "No medical treatment can offer an absolute guarantee. PEP greatly reduces the risk of HIV infection when started promptly and taken correctly for the full 28-day course, but it is not 100% effective. Follow-up testing confirms your status.",
  },
  {
    q: "Can I get a PEP consultation online?",
    a: "Yes. An urgent online consultation allows rapid assessment of your exposure and immediate guidance on whether and where to start PEP. Do not delay — book or call now.",
  },
];

const HivPep = () => (
  <>
    <SEO
      title="HIV PEP in Delhi — Urgent Consultation (72-Hour Window) | Dr. Ankur Lal"
      description="Possible recent HIV exposure? HIV PEP is time-sensitive — start as soon as possible, generally not after 72 hours. Urgent confidential PEP consultation in Delhi with Dr. Ankur Lal, Ex-STD Specialist, AIIMS New Delhi."
      path="/hiv-pep-delhi"
      jsonLd={{
        "@context": "https://schema.org",
        "@type": "MedicalWebPage",
        name: "HIV PEP Consultation in Delhi",
        description:
          "Urgent HIV PEP (post-exposure prophylaxis) assessment and consultation in Delhi. PEP should be started as soon as possible after a potential exposure, generally within 72 hours.",
        url: `${SITE_URL}/hiv-pep-delhi`,
      }}
    />

    <div className="pt-[72px]">
      <div
        className="bg-amber-500 text-slate-950"
        data-testid="pep-urgent-banner"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-center gap-3 text-center">
          <AlertTriangle className="w-4.5 h-4.5 w-[18px] h-[18px] shrink-0" />
          <p className="text-xs sm:text-sm font-bold tracking-wide uppercase">
            HIV PEP is time-sensitive — seek medical advice as soon as possible after a potential exposure
          </p>
        </div>
      </div>

      <section className="bg-[#FAF9F6] texture-grain py-16 sm:py-24">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal>
            <Eyebrow>Emergency HIV Prevention — Delhi</Eyebrow>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.08] tracking-tight text-slate-950">
              HIV PEP Consultation in Delhi
            </h1>
            <p className="mt-6 font-serif text-2xl sm:text-3xl text-emerald-950 leading-snug">
              Possible recent HIV exposure? Don't delay seeking medical advice.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
              PEP should be started as soon as possible after a potential HIV exposure and
              is generally not recommended after 72 hours. When clinically indicated, the
              standard course is 28 days.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/booking?type=hiv-pep"
                data-testid="pep-book-urgent"
                className="inline-flex items-center justify-center gap-2 bg-amber-600 hover:bg-amber-500 text-white font-semibold text-sm px-7 py-4 rounded-full transition-all shadow-lg hover:-translate-y-0.5"
              >
                <CalendarCheck className="w-4 h-4" /> Book Urgent HIV PEP Consultation
              </Link>
              <a
                href={`tel:${CLINIC.phoneRaw}`}
                data-testid="pep-call-now"
                className="inline-flex items-center justify-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-semibold text-sm px-7 py-4 rounded-full transition-all"
              >
                <PhoneCall className="w-4 h-4" /> Call Now — {CLINIC.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>

    <section className="py-14 sm:py-20" data-testid="pep-sections">
      <div className="max-w-4xl mx-auto px-5 sm:px-8 space-y-12">
        {SECTIONS.map((s, i) => (
          <Reveal key={s.h} delay={i * 0.04}>
            {s.urgent ? (
              <div className="bg-amber-50 border border-amber-300/60 rounded-3xl p-7 sm:p-9">
                <div className="flex items-center gap-3">
                  <Clock className="w-6 h-6 text-amber-700" />
                  <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-950">{s.h}</h2>
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed">{s.b}</p>
              </div>
            ) : (
              <div>
                <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-slate-950">{s.h}</h2>
                <p className="mt-4 text-slate-600 leading-relaxed">{s.b}</p>
              </div>
            )}
          </Reveal>
        ))}

        <Reveal>
          <div className="bg-white border border-slate-900/10 rounded-3xl p-7 sm:p-9">
            <div className="flex items-center gap-2.5 mb-4">
              <ShieldCheck className="w-5 h-5 text-emerald-900" />
              <h2 className="font-serif text-2xl font-semibold text-slate-950">Honest, responsible guidance</h2>
            </div>
            <ul className="space-y-3">
              {[
                "PEP greatly reduces the risk of HIV infection when started promptly and taken correctly — but it is not a guarantee, and follow-up testing is essential.",
                "PEP is generally considered when clinically indicated, following assessment of the exposure.",
                "The standard PEP course is 28 days, with follow-up HIV testing afterwards.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-sm text-slate-700 leading-relaxed">
                  <CheckCircle2 className="w-4 h-4 text-emerald-800 mt-0.5 shrink-0" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>

        <Reveal>
          <div className="bg-white border border-slate-900/10 rounded-3xl p-7 sm:p-9">
            <Eyebrow>PEP Questions</Eyebrow>
            <Accordion type="single" collapsible className="mt-4">
              {PEP_FAQS.map((f, i) => (
                <AccordionItem key={i} value={`pep-faq-${i}`} data-testid={`pep-faq-${i}`}>
                  <AccordionTrigger className="text-left font-medium text-slate-900">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </Reveal>
      </div>
    </section>

    <CtaBand
      title="Think You May Need PEP? Act Now."
      text="Book an urgent confidential consultation — or call the clinic immediately. Every hour matters."
    />
  </>
);

export default HivPep;
